import ImageFile from '@/components/tiny-image-file'
import JSZip from 'jszip'
import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { filesize } from 'filesize'
import { useLocalStorage } from '@buzuosheng/use-localstorage'

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import Header from '@/components/layouts/header'
import PasteImageButton from '@/components/paste-image-button'
import { useTranslations } from 'next-intl'
import { GetStaticProps } from 'next'
import { Upload } from 'lucide-react'
import { Seo } from '@/components/seo'

function TinyImage() {
  const t = useTranslations('TinyImage')
  const [files, setFiles] = useState<File[]>([])
  const [reduceSize, setReduceSize] = useState(0)
  const [compressFiles, setCompressFiles] = useState<File[]>([])
  const [target, setTarget] = useLocalStorage<undefined | string>('TinyImage:Target', { initialValue: 'webp', age: '10y' });
  const targetFormat = target ? ({
    webp: 'image/webp',
    avif: 'image/avif'
  })[target] : undefined

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(prevFiles => [...prevFiles, ...acceptedFiles.filter(file => !prevFiles.find(x => x.name === file.name))])
  }, [])

  const onCompress = useCallback((compressFile: File | Error, file: File) => {
    if (compressFile instanceof File) {
      setCompressFiles(prevFiles => [...prevFiles, compressFile])
      setReduceSize(prevSize => prevSize + file.size - compressFile.size)
    }
  }, [])

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/*': []
    }
  })

  const onPaste = useCallback((file: File) => {
    setFiles(prevFiles => [...prevFiles, file])
  }, [])

  const zipImage = useCallback(() => {
    const zip = new JSZip()

    for (const file of compressFiles) {
      zip.file(file.name, file, { binary: true })
    }

    zip.generateAsync({ type: 'blob' }).then(blob => {
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `DEVTOOL.TECH-TinyImage-${new Date().toISOString().slice(0, 10)}.zip`
      a.click()
    })
  }, [compressFiles])

  return (
    <div className="container mx-auto px-4">
      <Seo
        title={t('pageTitle')}
        description={t('pageDescription')}
        thumbnail="https://devtool.tech/thumbnail/tiny-image.png"
        path="/tiny-image"
      />
      <Header title={t('headerTitle')} />

      <div {...getRootProps()} className="p-4 text-center border-2 border-dashed rounded-lg cursor-pointer mt-4">
        <input {...getInputProps()} />
        <Upload className="w-12 h-12 mx-auto text-gray-400" />
        <p className="mt-2 text-sm text-gray-600">{t('dropzoneText')}</p>
        <PasteImageButton onPaste={onPaste} />
        <div className="mt-4 flex justify-center space-x-2">
          {['webp', 'avif'].map((x: string) => (
            <Badge
              key={x}
              variant={target === x ? "default" : "outline"}
              className="cursor-pointer"
              onClick={(e) => {
                e.stopPropagation()
                setTarget(target === x ? undefined : x)
              }}
            >
              {x}
            </Badge>
          ))}
        </div>
      </div>

      <div className="mt-8 space-y-4">
        {files.map(file => (
          <ImageFile
            target={targetFormat as any}
            file={file}
            key={file.name}
            onCompress={(compressFile) => onCompress(compressFile, file)}
          />
        ))}
      </div>

      {!!files.length && (
        <div className="mt-8 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {t.rich('savedTraffic', {
              size: (chunks) => <strong className="text-primary">{chunks}</strong>,
              sizeValue: filesize(reduceSize)
            })}
          </p>
          <Button onClick={zipImage}>{t('downloadZip')}</Button>
        </div>
      )}
    </div>
  )
}

export default TinyImage

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      messages: (await import(`../messages/${locale}.json`)).default
    }
  }
}